<template>
  <div
    :class="[
      `is-behaviour-${behaviour}`,
      `is-direction-${direction}`,
      { 'is-on-grid': isOnGrid },
    ]"
    class="atom-image__wrapper"
  >
    <UtilRouteLink v-if="link" :link="link">
      <div class="atom-image__background">
        <NuxtImg
          v-if="!isSvg && mergedProps.src"
          decoding="auto"
          class="atom-image"
          v-bind="{ ...mergedProps }"
          densities="x1 x2"
          :sizes="sizes"
          @load="onLoad()"
        />

        <img
          v-else-if="mergedProps.src"
          :src="mergedProps.src"
          :alt="mergedProps.alt"
          @load="onLoad()"
        />

        <div
          v-if="user"
          class="atom-image__avatar"
        >
          <AtomAvatar
            size="small"
            :initials="user.initials"
            background-color="var(--c-primary--light)"
          />

          {{ user.username }}
        </div>
      </div>
    </UtilRouteLink>

    <div v-else class="atom-image__background">
      <NuxtImg
        v-if="!isSvg && mergedProps.src"
        decoding="auto"
        class="atom-image"
        v-bind="{ ...mergedProps }"
        densities="x1 x2"
        :sizes="sizes"
        @load="onLoad()"
      />

      <img
        v-else-if="mergedProps.src"
        :src="mergedProps.src"
        :alt="mergedProps.alt"
        @load="onLoad()"
      />

      <div
        v-if="user"
        class="atom-image__avatar"
      >
        <AtomAvatar
          size="small"
          :initials="user.initials"
          background-color="var(--c-primary--light)"
        />

        {{ user.username }}
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    /* Image Data */
    image: {
        type: Object,
        required: true,
        validator: (value) => typeof value === 'object',
    },

    provider: {
        type: String,
        default: null,
    },

    /* Options */
    width: {
        type: [String, Boolean, Number],
        default: false,
    },

    height: {
        type: [String, Boolean, Number],
        default: false,
    },

    aspectRatio: {
        type: [String, Boolean],
        default: false,
    },

    lazy: {
        type: Boolean,
        default: true,
    },

    lazyDelay: {
        type: Number,
        default: 0,
    },

    background: {
        type: [String, Boolean],
        default: false,
    },

    preload: {
        type: Boolean,
        default: false,
    },

    /* Storyblok only */
    format: {
        type: String,
        default: 'webp',
        validator: (value) => ['webp', 'jpg', 'png'].includes(value),
    },

    smartCrop: {
        type: Boolean,
        default: false,
        validator: (value) => typeof (value) === 'boolean',
    },

    focus: {
        type: [String, Boolean],
        default: false,
    },

    sizes: {
        type: [String, Boolean],
        default: '1920px',
    },

    /*
          Layout
    */
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    behaviour: {
        type: String,
        default: 'default',
        validator: (value) => [
            'default',
            'cover',
            'contain',
            'stretch',
        ].includes(value),
    },

    direction: {
        type: String,
        default: 'width',
        validator: (value) => ['width', 'height'].includes(value),
    },

    /*
        Variants
    */
    user: {
        type: Object,
        default: null,
    },

    /*
        Styles
    */
    borderRadius: {
        type: String,
        default: 'var(--b-radius--none)',
    },

    /*
        Functionality
    */
    link: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['on-load']);
const onLoad = () => {
    emit('on-load');
};

const {
    borderRadius,
} = toRefs(props);

/* Lazy loading background */
const backgroundColor = computed(() => {
    if (props.background) {
        return props.background;
    }

    return 'transparent';
});

/*
      Build props for Nuxt Image component
  */
const generalProps = computed(() => ({
    provider: props.provider,
    loading: props.lazy ? 'lazy' : 'eager',
    preload: props.preload,
    sizes: props.sizes ? props.sizes : null,
}));

const buildStoryblokProps = () => {
    const modifiers = {
        filters: {},
    };

    if (props.smartCrop) {
        modifiers.smart = true;
    }

    if (props.focus && !props.smartCrop) {
        modifiers.filters.focal = props.focus;
    }

    const dimensions = {
        width: props.width ? props.width : null,
        height: props.height ? props.height : null,
    };

    if (dimensions.width && !dimensions.height) {
        const splittedUrl = props.image?.filename.split('/')[5].split('x');
        const originalWidth = parseInt(splittedUrl[0], 10);
        const originalHeight = parseInt(splittedUrl[1], 10);

        dimensions.width = originalWidth < dimensions.width ? originalWidth : dimensions.width;
        dimensions.height = getImageHeight(originalWidth, dimensions.width, originalHeight);
    }

    if (props.aspectRatio) {
        const width = parseInt(props.width, 10);
        const [ratioX, ratioY] = props.aspectRatio.split(':');
        dimensions.width = `${width}px`;
        dimensions.height = `${(width / ratioX) * ratioY}px`;
    }

    return {
        src: props.image?.filename,
        alt: props.image?.alt,
        format: props.format,
        modifiers,
        ...dimensions,
    };
};

const buildIpxProps = () => ({
    src: props.image?.src,
    alt: props.image?.alt,
    width: props.width ? props.width : null,
    height: props.height ? props.height : null,
});

const imageProps = computed(() => {
    switch (props.provider) {
    case 'storyblok':
        return buildStoryblokProps();
    case 'ipx':
    default:
        return buildIpxProps();
    }
});

const mergedProps = computed(() => ({
    ...generalProps.value,
    ...imageProps.value,
}));

/*
      Check for svg
  */
const isSvg = computed(() => mergedProps.value?.src?.includes('.svg'));
</script>

<style lang="scss" scoped>
.atom-image__wrapper {
    overflow: hidden;
    border-radius: v-bind(borderRadius);

    &.is-on-grid {
        @include wrapper-layout;
        @include grid-layout();
        @include fluid-simple('margin-bottom', 30px, 60px);
    }

    &.is-behaviour-cover,
    &.is-behaviour-contain,
    &.is-behaviour-stretch {
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 100%;
    }

    &.is-behaviour-stretch {
        position: absolute;
        width: 100%;
        height: 100%;
    }
}

.atom-image__background {
    position: relative;
    display: flex;
    width: 100%;
    align-self: flex-start;
    background: v-bind(backgroundColor);

    .is-direction-height & {
        width: auto;
        height: 100%;
    }

    .is-behaviour-cover &,
    .is-behaviour-contain &,
    .is-behaviour-stretch & {
        width: 100%;
        height: 100%;
    }

    img {
        width: 100%;

        .is-direction-height & {
            width: auto;
            height: 100%;
        }

        .is-behaviour-cover &,
        .is-behaviour-contain & {
            width: 100%;
            height: 100%;
        }

        .is-behaviour-cover & {
            object-fit: cover;
        }

        .is-behaviour-contain & {
            object-fit: contain;
        }

        .is-behaviour-stretch & {
            width: 100%;
            height: 100%;
        }
    }

    .is-on-grid & {
        @include default-content-columns;
    }
}
.atom-image {
    width: 100%;

    .is-direction-height & {
        width: auto;
        height: 100%;
    }
}

.atom-image__avatar {
    position: absolute;
    bottom: 0;
    left: 0;
    display: flex;
    width: 100%;
    align-items: center;
    padding: 10px 20px;
    border-radius: var(--b-radius--medium);
    background: var(--c-primary);
    color: var(--c-white);
    column-gap: 20px;
    font-family: var(--f-family--primary);
    font-size: var(--f-size--tab);
    font-weight: var(--f-weight--700);
    line-height: var(--l--height--tab);

    @include tablet {
        width: unset;
    }
}
</style>
